import React, { useMemo } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  EmptyState,
  Icon,
  Separator,
  Text,
} from "@salestrak/ui";

import type { MapPin } from "./map-types";
import { MapIssueCard } from "./map-issue-card";
import type { MapIssue } from "./map-issue";
import type { AddressDetails } from "#app/lib/hooks/use-map-infobox-data";
import { MapInfoboxDossier } from "./map-infobox-content";
import { openDirections } from "#app/lib/utils/open-directions";
import {
  directionsAddress,
  infoboxTitleBlock,
  propertyDetailGroups,
  resolvePropertyDetails,
} from "./map-property-details";

interface MapInfoboxSheetProps {
  pin: MapPin | null;
  issues: MapIssue[];
  addressDetails: AddressDetails | null;
  propertyDetailsLoading: boolean;
  /** The map's Property facts layer. Off = an empty address shows the
   *  empty state instead of the property list, and no property skeleton. */
  showPropertyFacts?: boolean;
  onDismiss: () => void;
  onIssueClick: (issue: MapIssue) => void;
  onAddProject: () => void;
}

/**
 * The mobile map infobox. `Drawer` is state-driven, so presence IS
 * `pin != null` — the caller holds no ref and there is nothing to present or
 * dismiss imperatively. `DrawerPopup` owns the bottom safe area. The title
 * block follows the desktop header's rules (the street wraps, the city line
 * truncates with the Project count leading), and under it the sheet keeps
 * the desktop popover's whole rail anatomy: one FULL-WIDTH hairline, the
 * Project cards or the same listed dossier on the same symmetric insets —
 * never a hairline inset to the list it fences — and a pinned footer with
 * the same two actions on the same rule (Add Project filled only on an
 * empty pin). The footer rides the software keyboard if one is up, so the
 * actions are never under the keys.
 */
export function MapInfoboxSheet({
  pin,
  issues,
  addressDetails,
  propertyDetailsLoading,
  showPropertyFacts = true,
  onDismiss,
  onIssueClick,
  onAddProject,
}: MapInfoboxSheetProps) {
  // Retain the last non-null pin so the dismiss animation plays with real
  // content after the parent clears selectedPin.
  const lastPinRef = React.useRef<MapPin | null>(pin);
  if (pin) lastPinRef.current = pin;
  const renderPin = pin ?? lastPinRef.current;

  const projectCount = renderPin?.issueIds.length ?? 0;
  const hasIssues = projectCount > 0;
  const { title, subtitle: cityLine } = renderPin
    ? infoboxTitleBlock(renderPin, addressDetails)
    : { title: "", subtitle: null };
  const destination = renderPin
    ? directionsAddress(renderPin, addressDetails)
    : null;
  const dossierRows = useMemo(
    () =>
      renderPin
        ? propertyDetailGroups(
            resolvePropertyDetails(renderPin, addressDetails),
          ).flatMap((group) => group.rows)
        : [],
    [addressDetails, renderPin],
  );
  // The dossier zone exists while there is (or may yet be) something to
  // read — the desktop popover's rule; its own skeleton covers the wait.
  const showDossier =
    !hasIssues &&
    showPropertyFacts &&
    (dossierRows.length > 0 || propertyDetailsLoading);

  return (
    <Drawer
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      open={pin != null}
    >
      {/* No scrim: the sheet annotates the map, it does not replace it —
          the pin stays in view above it, and a press on the map dismisses
          it, the way a place sheet works in Apple Maps and Google Maps. */}
      <DrawerPopup scrim={false} showBar>
        <DrawerHeader>
          <Box gap="2xs" shrink>
            <DrawerTitle>{title}</DrawerTitle>
            {projectCount > 1 || cityLine ? (
              <Box direction="row" align="center" gap={6} minWidth={0}>
                {projectCount > 1 ? (
                  <>
                    <Box shrink={false}>
                      <Text size="sm" variant="muted" truncate>
                        {projectCount} Projects
                      </Text>
                    </Box>
                    {cityLine ? (
                      <Text size="sm" variant="faint" aria-hidden>
                        ·
                      </Text>
                    ) : null}
                  </>
                ) : null}
                {cityLine ? (
                  <Text size="sm" variant="muted" truncate>
                    {cityLine}
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </DrawerHeader>
        {/* The panel's own inset is dropped so the rail runs edge to edge;
            each zone below lays its own gutter on the DrawerHeader's 24px
            edge (`lg`), never the desktop popover's narrower `base` — the
            sheet's one content edge is the header's. */}
        <DrawerPanel variant="flush">
          <Separator />
          {hasIssues ? (
            // id: mirrors the desktop overlay's e2e locator hook.
            <Box gap="xs" id="map-infobox" paddingX="lg" paddingY="sm">
              {issues.map((issue) => (
                <MapIssueCard
                  key={issue.issueId}
                  issue={issue}
                  onClick={onIssueClick}
                />
              ))}
            </Box>
          ) : showDossier ? (
            <MapInfoboxDossier
              rows={dossierRows}
              loading={propertyDetailsLoading}
              inset="lg"
            />
          ) : (
            <Box paddingY="md">
              <EmptyState
                size="xs"
                title={
                  showPropertyFacts
                    ? "No property details available"
                    : "No Projects at this address"
                }
              />
            </Box>
          )}
        </DrawerPanel>
        <DrawerFooter aboveKeyboard>
          <Box direction="row" gap="xs">
            <Box direction="column" fill="flex">
              <Button
                onClick={onAddProject}
                shape="square"
                size="xl"
                variant={hasIssues ? "outline" : "solid"}
              >
                <Icon name="plus" />
                Add Project
              </Button>
            </Box>
            <Box direction="column" fill="flex">
              <Button
                disabled={!destination}
                onClick={() => {
                  if (destination) openDirections(destination);
                }}
                shape="square"
                size="xl"
                variant="outline"
              >
                <Icon name="navigation" />
                Directions
              </Button>
            </Box>
          </Box>
        </DrawerFooter>
      </DrawerPopup>
    </Drawer>
  );
}
